import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timezone, TypeGuard, toFloat } from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  KEYSET_PLACEHOLDER,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';
import { EXACT_NULL_SENTINEL } from '../../util/helpers';
import { ExactV2HolderSplit } from '../../lib/exact-v2-holder-split';

export const TRANSACTION_ALLOCATIONS_V2_RESOURCE_TYPE =
  'transactionAllocationV2';

export const TRANSACTION_ALLOCATIONS_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Transaction Allocations V2 List',
      description: '',
      idPrefix: TRANSACTION_ALLOCATIONS_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

/**
 * The double-entry allocation table (TRNSALLO) — each row pairs a payment
 * (receipt/adjustment/transfer) with the charge it settles and the amount
 * allocated. Replaces V1's greedy oldest-first allocator with deterministic
 * links.
 *
 * Joins back to the flat ledger (TRANS4) via DRTrnsId (the payment) and
 * CRTrnsId (the charge being settled). InvlLine + InvLineDataIndex point
 * at the specific invoice line (INVLDNT5) being settled, when known.
 */

export interface IExactV2TransactionAllocationResult {
  id: string;
  /**
   * Re-attributed patient (debtor) id, derived from the charge-side (CR)
   * ledger row. Agreement-holder debtor ids are rewritten to the real patient
   * via the cot_to_real_debtor CTE — identical logic to the transactions query.
   */
  patient_id: string;
  /**
   * Payment-side ledger row id (DR). Split holder receipts carry their
   * `<receiptId>-<patientId>` id here.
   */
  payment_transaction_id: string;
  payment_transaction_type: number;
  payment_date: string;
  /** Charge-side ledger row id (CR). The charge being settled. */
  charge_transaction_id: string;
  charge_transaction_type: number;
  charge_date: string;
  allocation_date: string;
  /** Amount allocated from this payment to this charge (typically negative). */
  amount: number;
  gst_amount: number;
  /** Invoice line being settled — undefined when not line-specific. */
  invoice_line_number?: number;
  invoice_line_data_index?: number;
  /** Reversing transaction id if this allocation was voided. */
  closing_transaction_id?: string;
  location?: string;
}

export interface IExactV2TransactionAllocation {
  id: string;
  /**
   * Re-attributed patient id, scoped to the charge (CR) side of this
   * allocation row. Matches the `patientId` on the corresponding charge
   * transaction in the transactions-v2 source entity.
   */
  patientId: string;
  paymentTransactionId: string;
  paymentTransactionType: number;
  paymentDate: string;
  chargeTransactionId: string;
  chargeTransactionType: number;
  chargeDate: string;
  allocationDate: string;
  amount: number;
  gstAmount: number;
  invoiceLineNumber?: number;
  invoiceLineDataIndex?: number;
  closingTransactionId?: string;
  location?: string;
}

export interface IExactV2TransactionAllocationFilters {
  patientId: string;
  paymentTransactionId: string;
  chargeTransactionId: string;
}

function isExactV2TransactionAllocation(
  data: unknown,
  logError: boolean
): data is IExactV2TransactionAllocation {
  return TypeGuard.interface<IExactV2TransactionAllocation>(
    {
      id: isString,
      patientId: isString,
      paymentTransactionId: isString,
      paymentTransactionType: isNumber,
      paymentDate: isString,
      chargeTransactionId: isString,
      chargeTransactionType: isNumber,
      chargeDate: isString,
      allocationDate: isString,
      amount: isNumber,
      gstAmount: isNumber,
      invoiceLineNumber: TypeGuard.nilOr(isNumber),
      invoiceLineDataIndex: TypeGuard.nilOr(isNumber),
      closingTransactionId: TypeGuard.nilOr(isString),
      location: TypeGuard.nilOr(isString),
    },
    logError
  )(data);
}

const TRANSACTION_ALLOCATIONS_V2_SOURCE_QUERY = `
WITH agreement_holders AS MATERIALIZED (
  SELECT DISTINCT payorid AS debtorid
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND payorid != ${EXACT_NULL_SENTINEL}
    AND payorid NOT IN (SELECT _record_id FROM localpayor)
  UNION
  SELECT _record_id AS debtorid FROM localpayor
),
cot_to_real_debtor AS MATERIALIZED (
  SELECT
    cotguid AS cot_guid,
    MIN(debtorid) AS real_debtor_id
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND debtorid != ${EXACT_NULL_SENTINEL}
    AND cotguid != ${EXACT_NULL_SENTINEL}
    AND debtorid NOT IN (SELECT debtorid FROM agreement_holders)
  GROUP BY cotguid
),
-- Holder payment rows the transactions query splits per patient.
holder_split_payments AS MATERIALIZED (
  SELECT trans4._record_id AS id, trans4.date
  FROM trans4
  LEFT JOIN cot_to_real_debtor payment_cot
    ON payment_cot.cot_guid = trans4.cotguid
  WHERE
    NOT COALESCE(trans4._is_deleted, false)
    AND ${ExactV2HolderSplit.isSplitPayment('trans4', 'payment_cot')}
)
SELECT
  a._record_id::TEXT          AS id,

  resolved.patient_id         AS patient_id,

  -- Point at the split covering this charge, not the undivided holder receipt.
  CASE
    WHEN EXISTS (
        SELECT 1
        FROM holder_split_payments
        WHERE holder_split_payments.id = a.DRTrnsId
          AND holder_split_payments.date = a.DR_date
      )
    THEN ${ExactV2HolderSplit.splitId('a.DRTrnsId', 'resolved.patient_id')}
    ELSE a.DRTrnsId::TEXT
  END                           AS payment_transaction_id,
  convert_to_integer(a.DR_trnstype) AS payment_transaction_type,
  -- Dated like the transactions query, which falls back to the entry date.
  COALESCE(
    NULLIF(a.DR_date::TEXT, ''),
    (
      SELECT dr_trans.entry_date::TEXT
      FROM trans4 dr_trans
      WHERE dr_trans._record_id = a.DRTrnsId
        AND dr_trans.date = a.DR_date
      LIMIT 1
    )
  )                             AS payment_date,
  a.CRTrnsId::TEXT              AS charge_transaction_id,
  convert_to_integer(a.CR_trnstype) AS charge_transaction_type,
  COALESCE(NULLIF(a.CR_date::TEXT, ''), cr_trans.entry_date::TEXT) AS charge_date,
  a.Date::TEXT                  AS allocation_date,
  convert_to_decimal(a.Amount)  AS amount,
  COALESCE(convert_to_decimal(a.GstAmount), 0) AS gst_amount,
  convert_to_integer(NULLIF(a.InvlLine::TEXT, '0'))          AS invoice_line_number,
  convert_to_integer(NULLIF(a.InvLineDataIndex::TEXT, '0'))  AS invoice_line_data_index,
  NULLIF(a.ClosingTrnsId::TEXT, '0')    AS closing_transaction_id,
  NULLIF(a.Location, '')      AS location
FROM
  trnsallo a
  JOIN trans4 cr_trans
    ON cr_trans._record_id = a.CRTrnsId
    AND cr_trans.date = a.CR_date
  LEFT JOIN cot_to_real_debtor cot_cr
    ON cot_cr.cot_guid = cr_trans.cotguid
  -- Charge (CR) side, re-attributed the same way as the transactions query.
  CROSS JOIN LATERAL (
    SELECT COALESCE(
      CASE
        WHEN cr_trans.debtorid IN (SELECT debtorid FROM agreement_holders)
        THEN cot_cr.real_debtor_id
        ELSE NULL
      END,
      cr_trans.debtorid
    )::TEXT AS patient_id
  ) resolved
WHERE
  COALESCE(a._is_deleted, false) = false
${KEYSET_PLACEHOLDER}
`;

export type ExactV2TransactionAllocation = ISourceEntityTypes<
  IExactV2TransactionAllocation,
  object,
  IExactV2TransactionAllocationFilters
>;

export class TransactionAllocationsV2SourceEntity extends BaseSourceEntity<ExactV2TransactionAllocation> {
  sourceEntity = TRANSACTION_ALLOCATIONS_V2_SOURCE_ENTITY;
  entityResourceType = TRANSACTION_ALLOCATIONS_V2_RESOURCE_TYPE;
  sourceQuery = TRANSACTION_ALLOCATIONS_V2_SOURCE_QUERY;
  verifySourceFn = isExactV2TransactionAllocation;
  override defaultOffsetSize = 100000;
  allowOffsetJobs = true;
  override keysetPagination = { cursorColumn: 'a._record_id' };

  override transformDataFn = flow([
    convertNullToUndefinedFn<IExactV2TransactionAllocationResult>(),
    convertValueFn(toFloat, 'amount', 'gst_amount'),
    convertKeysToCamelCaseFn<IExactV2TransactionAllocationResult>(),
  ]);

  getSourceRecordId(data: IExactV2TransactionAllocation): string {
    return data.id;
  }

  getSourceLabel(record: IExactV2TransactionAllocation): string {
    return `payment:${record.paymentTransactionId} - charge:${record.chargeTransactionId}`;
  }

  translate(_data: IExactV2TransactionAllocation, _timezone: Timezone): object {
    return {};
  }

  getFilterData(
    data: IExactV2TransactionAllocation,
    _timezone: Timezone
  ): IExactV2TransactionAllocationFilters {
    return {
      patientId: data.patientId,
      paymentTransactionId: data.paymentTransactionId,
      chargeTransactionId: data.chargeTransactionId,
    };
  }
}
